import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  contagemLegivel,
  destinoSeguro,
  quandoLegivel,
  rotuloDoSino,
  useDefinirEmail,
  useMarcarComoLida,
  useMarcarTodasComoLidas,
  usePreferenciaDeEmail,
  useSino,
  type Notificacao,
} from "../../nucleo/notificacoes";
import { classes } from "../../nucleo/classes";
import { Botao } from "../ui";

function Sininho() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3a6 6 0 0 0-6 6v3.2L4.4 15a1 1 0 0 0 .9 1.5h13.4a1 1 0 0 0 .9-1.5L18 12.2V9a6 6 0 0 0-6-6Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M9.8 19a2.4 2.4 0 0 0 4.4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ItemDoSino({
  item,
  aoAbrir,
  aoMarcar,
}: {
  item: Notificacao;
  aoAbrir: (item: Notificacao) => void;
  aoMarcar: (item: Notificacao) => void;
}) {
  const destino = destinoSeguro(item.link);
  const quando = quandoLegivel(item.criada_em);
  return (
    <li className={classes("notif", !item.lida && "notif--nova")} data-testid="notificacao">
      <p className="notif__titulo">
        {item.titulo}
        {!item.lida ? <span className="sr-only"> (nova)</span> : null}
      </p>
      <p className="notif__quando">{[item.ator_nome, quando].filter(Boolean).join(" · ")}</p>
      {item.corpo ? <p className="notif__corpo">{item.corpo}</p> : null}
      <div className="notif__acoes">
        {destino ? (
          <Link to={destino} aria-label={`Abrir: ${item.titulo}`} onClick={() => aoAbrir(item)}>
            Abrir
          </Link>
        ) : null}
        {!item.lida ? (
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            aria-label={`Marcar como lida: ${item.titulo}`}
            onClick={() => aoMarcar(item)}
          >
            Marcar como lida
          </Botao>
        ) : null}
      </div>
    </li>
  );
}

/**
 * O sino do topo de toda tela, nos dois lados (cliente e equipe). Recebe tudo; só o e-mail se
 * desliga. A contagem vem do servidor — a tela não conta nada — e o painel abre sobre a página,
 * fecha com Esc ou clique fora e devolve o foco ao botão.
 */
export function SinoDeNotificacoes() {
  const painelId = useId();
  const raiz = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const [aberto, setAberto] = useState(false);
  const sino = useSino();
  const marcar = useMarcarComoLida();
  const marcarTodas = useMarcarTodasComoLidas();
  const preferencia = usePreferenciaDeEmail(aberto);
  const definirEmail = useDefinirEmail();

  useEffect(() => {
    if (!aberto) return;
    function fora(evento: MouseEvent) {
      if (raiz.current && !raiz.current.contains(evento.target as Node)) setAberto(false);
    }
    function esc(evento: KeyboardEvent) {
      if (evento.key !== "Escape") return;
      setAberto(false);
      botao.current?.focus();
    }
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto]);

  const naoLidas = sino.data?.nao_lidas ?? 0;
  // O que a pessoa acabou de marcar aparece na hora, no mesmo quadro de tela do clique; ao
  // fim do pedido volta a valer o que o servidor diz (inclusive se ele recusou).
  const [marcadoAgora, setMarcadoAgora] = useState<boolean | null>(null);
  const emailLigado = marcadoAgora ?? preferencia.data?.email_ligado ?? true;

  return (
    <div className="sino" ref={raiz} data-testid="sino">
      <button
        ref={botao}
        type="button"
        className="sino__botao"
        aria-label={rotuloDoSino(naoLidas)}
        aria-expanded={aberto}
        aria-controls={painelId}
        onClick={() => setAberto((atual) => !atual)}
      >
        <Sininho />
        {naoLidas > 0 ? (
          <span className="sino__contagem" data-testid="sino-contagem" aria-hidden="true">
            {contagemLegivel(naoLidas)}
          </span>
        ) : null}
      </button>

      {aberto ? (
        <section id={painelId} className="sino__painel" aria-label="Notificações">
          <div className="sino__cabeca">
            <h2 className="sino__titulo">Notificações</h2>
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              disabled={naoLidas === 0 || marcarTodas.isPending}
              onClick={() => marcarTodas.mutate()}
            >
              Marcar todas como lidas
            </Botao>
          </div>

          {sino.isError ? (
            <p className="sino__vazio" role="alert">
              {mensagemDoConsole(sino.error)}
            </p>
          ) : sino.isPending ? (
            <p className="sino__vazio" role="status">
              Carregando…
            </p>
          ) : sino.data.itens.length === 0 ? (
            <p className="sino__vazio" role="status">
              Nada de novo por aqui.
            </p>
          ) : (
            <ul className="sino__lista" aria-label="Lista de notificações">
              {sino.data.itens.map((item) => (
                <ItemDoSino
                  key={item.id}
                  item={item}
                  aoAbrir={(aberta) => {
                    if (!aberta.lida) marcar.mutate(aberta.id);
                    setAberto(false);
                  }}
                  aoMarcar={(alvo) => marcar.mutate(alvo.id)}
                />
              ))}
            </ul>
          )}

          <div className="sino__rodape">
            <label>
              <input
                type="checkbox"
                checked={emailLigado}
                disabled={preferencia.isPending}
                onChange={(evento) => {
                  const ligado = evento.target.checked;
                  setMarcadoAgora(ligado);
                  definirEmail.mutate(ligado, { onSettled: () => setMarcadoAgora(null) });
                }}
              />
              Receber também por e-mail
            </label>
            <p>O sino recebe tudo e não se desliga. O e-mail traz só o essencial.</p>
          </div>
        </section>
      ) : null}
    </div>
  );
}
